/* eslint-disable @typescript-eslint/no-explicit-any */

import type { JSX } from 'react';
import React, { useEffect, useMemo, useState } from 'react';

import Box from '@mui/material/Box';
import { getIn } from 'formik';

import { withAPIService } from '../../networking';
import { withStore } from '../../store';
import { updateField } from '../../store/customer';
import { clearFieldError } from '../../store/fieldError';
import { BRACKET_REGEX } from '../../utils/constants';
import { getArrayItemFromName } from '../../utils/hooksLib';
import Autocomplete from '../Autocomplete';
import { ErrorFieldCheck } from '../ErrorValidation';
import { shouldRunUseEffect } from './util';

function createAddressString({
  street_line,
  city,
  state,
  zipcode
}: any): string {
  return `${street_line} ${city}, ${state} ${zipcode}`;
}

const blankAddress = {
  street_line: '',
  city: '',
  state: '',
  zipcode: ''
};

// Maps SmartyStreets fields to app fields
const addressFieldMapper: Record<string, string> = {
  street: 'street_line',
  city: 'city',
  state: 'state',
  zipCode: 'zipcode'
};

function shouldRunOnBlur(e: React.FocusEvent<HTMLInputElement>): boolean {
  return (
    !e.relatedTarget ||
    (e.relatedTarget &&
      e.target &&
      e.target.name !==
        (e.relatedTarget as HTMLInputElement).name)
  );
}

const Component = ({
  additionalProps = {},
  name,
  dispatch,
  store,
  apiService,
  label,
  required,
  formik,
  footer
}: any): JSX.Element => {
  const [addressInputValue, setAddressInputValue] =
    useState('');
  const [addresses, setAddresses] =
    useState<any[] | undefined>();
  const [hasErrorMessage, setHasErrorMessage] =
    useState('');
  const [hasSelected, setHasSelected] =
    useState(false);
  const [isTouch, setIsTouch] = useState(false);
  const [isDirty, setFieldDirty] =
    useState(false);

  const isArray = useMemo(
    () => name.match(BRACKET_REGEX),
    [name]
  );

  const country = useMemo(() => {
    if (isArray) {
      const arrayItem = getArrayItemFromName(
        store,
        name,
        parseInt(isArray[1], 10)
      );
      const parent = name.split('.');
      return getIn(arrayItem, `${parent[1]}.country`);
    }

    const splitName = name.split('.');
    splitName.pop();
    return getIn(
      store.customer,
      `${splitName.join('.')}.country`
    );
  }, [name, store, isArray]);

  useEffect(() => {
    if (getIn(store.customer, name) !== addressInputValue) {
      setAddressInputValue(
        getIn(store.customer, name) || ''
      );
      setHasSelected(true);
    }
  }, [store]);

  useEffect(() => {
    if (
      !hasSelected &&
      shouldRunUseEffect(
        country,
        addressInputValue,
        getIn(store.customer, name)
      )
    ) {
      apiService
        .getSmartyStreets(addressInputValue)
        .then((data: any) => {
          setAddresses(
            data.suggestions ? data.suggestions : []
          );
        })
        .catch(() => setAddresses([]));
    }
  }, [
    addressInputValue,
    apiService,
    country,
    hasSelected,
    label,
    name,
    store.customer
  ]);

  function updateFields(fieldName: string, value: any) {
    formik.form.setFieldValue(fieldName, value);
    dispatch(updateField({ name: fieldName, value }));
  }

  const errorMessage = getIn(formik.form.errors, name);
  const hasError = errorMessage !== undefined;

  function updateAddressInStore(address = blankAddress) {
    const split = name.split('.');
    let addressType = split[0];
    if (split.length > 2) {
      addressType = `${split[0]}.${split[1]}`;
    }

    return new Promise<void>(resolve => {
      Object.keys(addressFieldMapper).forEach(field => {
        dispatch(
          updateField({
            name: `${addressType}.${field}`,
            value:
              (address as any)[addressFieldMapper[field]]
          })
        );
      });
      setAddresses(undefined);
      resolve();
    });
  }

  function updateAddressInFormik(address = blankAddress) {
    const split = name.split('.');
    let addressType = split[0];
    if (split.length > 2) {
      addressType = `${split[0]}.${split[1]}`;
    }

    Object.keys(addressFieldMapper).forEach(field => {
      setTimeout(() => {
        formik.form.setFieldValue(
          `${addressType}.${field}`,
          (address as any)[addressFieldMapper[field]]
        );
        dispatch(
          clearFieldError(`${addressType}.${field}`)
        );
      }, 0);
    });

    setAddresses(undefined);
  }

  async function onBlur() {
    if (country !== 'US' || !hasSelected) {
      updateFields(name, addressInputValue);

      if (
        addressInputValue !== '' &&
        getIn(store.fieldError, name)
      ) {
        dispatch(clearFieldError(name));
      }

      if (!hasErrorMessage && !hasError) {
        await apiService.updateCustomer(
          store.customer,
          dispatch
        );
      }

      setFieldDirty(false);
    }
  }

  async function onSelect(selectedValue: string) {
    setHasSelected(true);

    const address = (addresses || []).find(
      add => createAddressString(add) === selectedValue
    );

    if (!address) return;

    setAddressInputValue(address.street_line);
    await updateAddressInStore(address);

    if (!hasErrorMessage && !hasError) {
      await apiService.updateCustomer(
        store.customer,
        dispatch
      );
    }

    updateAddressInFormik(address);
    setAddresses(undefined);
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: '100%',
        paddingTop: 'var(--spacing-sm)',
        marginBottom: 'var(--spacing-lg)'
      }}
    >
      <Box sx={{ width: '100%' }}>
        <Autocomplete
          data-testid="autocomplete"
          label={label || ''}
          required={required}
          footer={footer}
          onBlur={e => {
            setIsTouch(true);
            if (isDirty) {
              formik.form.setFieldTouched(name, true);
              shouldRunOnBlur(e) && onBlur();
            }
          }}
          onInputChange={e => {
            const err = getIn(formik.form.errors, name);
            setHasErrorMessage(err);

            const { value } = e.target;
            formik.form.setFieldValue(name, value);
            dispatch(updateField({ name, value }));

            setFieldDirty(true);
            setHasSelected(false);
            setAddressInputValue(value);
          }}
          name={name}
          inputValue={addressInputValue}
          placeholder={additionalProps.placeholder}
          suggestions={addresses}
          formatSuggestion={createAddressString}
          onSelect={onSelect}
          formik={formik}
          dispatch={dispatch}
          store={store}
          apiService={apiService}
        />
      </Box>

      {!window.location.href.includes('review') && (
        <ErrorFieldCheck
          hasError={hasError}
          isTouch={isTouch}
          errorMessage={errorMessage}
          setIsTouch={setIsTouch}
        />
      )}
    </Box>
  );
};

const connectAPI = withAPIService(Component);
const AddressAutocomplete = withStore(connectAPI);

export default AddressAutocomplete;